<template>
    <main class="main" :class="{ 'overflow-hidden': modals?.length }" @mousedown="startClickOutside" @mouseup="endClickOutside">
      <!-- <main class="main" @click="handleClickOutside" :class="{ 'overflow-hidden': modals?.length }"> -->
      <SidebarComponent
        v-if="isAuthenticated && !hideSidebar && !isRestrictedRoute"
        :active="activeMenu"
        :is-employer="isEmployer"
        :is-local="isLocal"
        @open-menu="openMenu"
      />
      <MobileHeader
        v-if="isAuthenticated && !isRestrictedRoute"
        @openModal="openModal"
        @open-menu="openMenu"
      />

      <section
        class="main-content"
        :class="{ 'authenticated': isAuthenticated && !isRestrictedRoute, 'fixed': activeMenu, 'admin': isSuperAdmin, 'bg-sixth' : isLoginPage }"
        id="content"
      >
        <HeaderComponent
          v-if="!hideHeader && showHeader && !isRestrictedRoute"
          @openModal="openModal"
          @click.stop
        />
        <router-view />
      </section>

      <!-- Render Modals -->
      <div class="modal-container" @click.stop>
        <ModalComponent
          v-for="(modal, index) in modals"
          :key="index"
          :isVisible="modal.isVisible"
          :index="index"
          :modalType="modal.type"
          :title="modal.title"
          :classNames="modal.classes"
          :fund="modal.fund"
          :modalProps="modal.modalProps"
          :secondTitle="modal.secondTitle"
          :headerButtons="modal.headerButtons"
          :hasCloseConfirm="modal.hasCloseConfirm"
          @close="closeModal"
          @close-above="closeAbove"
          @handle-submit="handleSubmit"
        >
          <template #steps v-if="modal.type === 'add-dependent' || modal.type === 'assign-fund'">
            <MultiStep :steps="2" :activeStep="currentStep" classes="steps-wrapper_header" />
          </template>
        </ModalComponent>
      </div>
      <div v-if="modals?.length || activeMenu" class="modal-overlay"></div>
    </main>
  </template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useModalStore } from '@/stores/modal';
import { useDependentStore } from '@/stores/dependent';
import { useParticipantStore } from './stores/participant';
import { useRoute } from 'vue-router';

import HeaderComponent from './components/HeaderComponent.vue';
import ModalComponent from './components/ModalComponent.vue';
import SidebarComponent from './components/sidebar/SidebarComponent.vue';
import MobileHeader from './components/sidebar/MobileHeader.vue';
import MultiStep from './components/form/MultiStep.vue';

export default defineComponent({
  components: {
    HeaderComponent,
    SidebarComponent,
    MobileHeader,
    ModalComponent,
    MultiStep
  },

  setup() {
    const authStore = useAuthStore();
    const modalStore = useModalStore();
    const dependentStore = useDependentStore();
    const route = useRoute();
    const isAuthenticated = computed(() => {
        if(authStore.user?.active2FA) {
            return authStore.isAuthenticated && authStore.twoFaValidated && authStore.termsAccepted;
        }

        return authStore.isAuthenticated && authStore.termsAccepted;
    });
    const restrictedRoutes = ['2fa.setup', '2fa.verify', 'terms', 'login', 'register', 'reset-new-password'];
    const isLoginPage = computed(() => route.name === 'login' || route.name === 'register');
    const isRestrictedRoute = computed(() => restrictedRoutes.includes(route.name as string));
    const modals = computed(() => modalStore.modals);
    const showHeader = computed(() => !route.path.startsWith('/file'));
    const activeMenu = ref(false);
    const isClickOutside = ref(false);
    const currentStep = computed(() => useParticipantStore().currentStep);
    const isSuperAdmin = computed(() => authStore.isSuperAdmin);
    const isLocal = computed(() => authStore.isLocal);
    const isEmployer = computed(() => authStore.isEmployer);

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Handle Click outside modals
      if (!target.closest('.modal-container') && !target.closest('.plan-modal')) {
        modalStore.closeAllModals();
      }

      // Handle Click outside Mobile Menu
      if (!target.closest('.mobile-menu') && !target.closest('.main-sidebar') && !target.closest('.plan-modal')) {
        activeMenu.value = false;
      }
    };

    const startClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Handle Click outside modals
      if (!target.closest('.modal-container') && !target.closest('.plan-modal') && !target.closest('.multiselect')) {
        isClickOutside.value = true;
      }

      // Handle Click outside Mobile Menu
      if (!target.closest('.mobile-menu') && !target.closest('.main-sidebar') && !target.closest('.plan-modal')) {
        activeMenu.value = false;
      }
    }

    const endClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the mouse was released outside the modal
      if (isClickOutside.value && !target.closest('.plan-modal') && !target.closest('.multiselect')) {
        modalStore.closeAllModals();
      }

      isClickOutside.value = false; // Reset flag
    }

    const areAllModalsClosed = computed(() => modalStore.modals.length === 0);

    watch(areAllModalsClosed, (newVal) => {
      const body = document.body;

      if (newVal) {
        body.classList.remove('overflow-disabled');
      } else {
        body.classList.add('overflow-disabled');
      }
    });

    const openMenu = () => {
      activeMenu.value = !activeMenu.value;
    }

    const closeAbove = (index: number) => {
      modalStore.closeAbove(index);
    }

    const hiddenRoutesSidebar = ['/login', '/authorization/login', '/authorization/verify', '/authorization/setup'];
    const hiddenRoutesHeader = ['/login', '/authorization/login', '/authorization/verify', '/authorization/setup'];

    const hideSidebar = computed(() => hiddenRoutesSidebar.includes(route.path));
    const hideHeader = computed(() => hiddenRoutesHeader.includes(route.path));

    return {
      isLoginPage,
      isRestrictedRoute,
      isAuthenticated,
      isSuperAdmin,
      isLocal,
      isEmployer,
      modals,
      activeMenu,
      currentStep,
      showHeader,
      openMenu,
      closeAbove,
      openModal: modalStore.openModal,
      closeModal: modalStore.closeModal,
      handleSubmit: modalStore.handleSubmit,
      handleClickOutside,
      startClickOutside,
      endClickOutside,
      hideSidebar,
      hideHeader
    };
  }
})
</script>